import { currencyFormatter, formatDate } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import Card from "@/components/custom/card";
import { Currency, Transaction, Vendor } from "@/lib/types";
import { Mixpanel } from "@/lib/mixpanel";
import { useMemo } from "react";
import { TRANSACTION_STATUS } from "@/lib/constants";
import Boat from "@/components/icons/boat";
import { useTranslation } from "react-i18next";
import { useHasPermission } from "@/hooks/use-permission";
import { PERMISSIONS } from "@/lib/constants/permissions";
import SwipeableRow from "@/components/custom/swipeable-row";

const TransactionCard = ({
  transaction,
  currencies,
  vendors,
  onDelete,
  redirectPath,
}: {
  transaction: Transaction;
  currencies: Currency[] | undefined;
  vendors: Vendor[] | undefined;
  onDelete?: (transaction: Transaction) => void;
  redirectPath?: string;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const canWriteExpenses = useHasPermission(PERMISSIONS.Expenses.Write);

  const currency = useMemo(() => {
    if ("currencyId" in transaction) {
      return currencies?.find(
        (currency) => currency.id === transaction.currencyId
      );
    }
  }, [currencies, transaction]);

  const convertedCurrency = useMemo(() => {
    if ("convertedCurrencyId" in transaction) {
      return currencies?.find(
        (currency) => currency.id === transaction.convertedCurrencyId
      );
    }
  }, [currencies, transaction]);

  const transactionStatus = useMemo(() => {
    return TRANSACTION_STATUS.find(
      (status) => status.value === transaction.status
    );
  }, [transaction.status]);

  const vendor = useMemo(() => {
    if ("vendorId" in transaction) {
      return vendors?.find((vendor) => vendor.id === transaction.vendorId);
    }
  }, [vendors, transaction]);

  const card = (
    <Card
      data-testid={`transaction-card-${transaction.id}`}
      onClick={() => {
        navigate(`/transaction/${transaction.id}`, {
          state: {
            redirectPath,
          },
        });
        Mixpanel.track("Navigate to Expense Details");
      }}
    >
      <div className="flex flex-row items-center justify-center gap-2">
        <span className="flex items-center justify-center text-white/50">
          <Boat color="#ffffff50" size={20} />
        </span>

        <div className="flex flex-col items-start justify-start gap-2">
          {vendor && (
            <span className="line-clamp-1 text-ellipsis text-sm font-medium leading-[14px] text-white">
              {vendor.name}
            </span>
          )}
          <div className="flex items-center gap-2">
            {transaction.type && (
              <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-medium text-white/80">
                {t(`label.${transaction.type.toLowerCase()}`, {
                  defaultValue: transaction.type,
                })}
              </span>
            )}
            <span className="text-xs font-medium leading-[14px] text-white/50">
              {formatDate(transaction.transactionDate, "dd MMMM yyyy, EE")}
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-row items-center gap-4">
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-[6px]">
            <span className="whitespace-nowrap text-sm font-medium leading-4 text-[#7ECEE8]">
              {currencyFormatter(transaction.amount, currency?.code)}
            </span>
            <span style={{ color: transactionStatus?.color }}>
              {transactionStatus?.icon}
            </span>
          </div>

          {"convertedAmount" in transaction &&
            transaction.conversionRate &&
            convertedCurrency && (
              <div className="flex items-center gap-1">
                <span className="whitespace-nowrap text-xs font-medium text-white/60">
                  {currencyFormatter(
                    transaction.convertedAmount,
                    convertedCurrency?.code
                  )}
                </span>
                <span className="text-[10px] text-white/40">
                  ({t("label.rate")}: {transaction.conversionRate.toFixed(6)})
                </span>
              </div>
            )}
        </div>
      </div>
    </Card>
  );

  if (onDelete && canWriteExpenses) {
    return (
      <SwipeableRow
        onDelete={() => onDelete(transaction)}
        deleteTestId="delete-transaction-button"
        deleteLabel={t("button.delete_transaction")}
      >
        {card}
      </SwipeableRow>
    );
  }

  return card;
};

export default TransactionCard;
